import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

/**
 * The certification menu, in the order of the SII's own steps:
 *
 * 1. `status` — «Estado de certificación» (where the company is, and what next).
 * 2. `onboarding` — «Empresa y credenciales» (view 13: company, certificate, CAFs).
 * 3. `test-set` — «Set de pruebas», tabs over: emit, runs (view 14), libros
 *    (view 16) and declaring the avance.
 * 4. `simulation` — «Simulación» (view 15), steps over: simulate, runs and
 *    declaring the avance.
 * 5. `intercambio` — «Intercambio de información».
 * 6. `muestras` — «Muestras impresas».
 * 7. `declaracion` — «Declaración de cumplimiento», tabs over the requirements
 *    and the cuadratura (view 17), which is the evidence of its letter f).
 *
 * Runs and libros have no path `:id`: a run or a libro is opened through
 * `?id=` in the URL. The paths they had before this order (`/certification/runs`,
 * `/libros`, `/certification/avance`, `/cuadratura`) redirect, in
 * `legacy-redirects.routes.tsx`.
 */
export const certificationRoutes: RouteObject[] = [
  { path: 'certification', element: <Navigate to="/certification/status" replace /> },
  {
    path: 'certification/status',
    lazy: () =>
      import('../pages/CertificationAvancePage').then((m) => ({
        Component: m.CertificationStatusPage,
      })),
  },
  {
    path: 'certification/onboarding',
    lazy: () =>
      import('../pages/OnboardingWizardPage').then((m) => ({
        Component: m.OnboardingWizardPage,
      })),
  },
  {
    path: 'certification/test-set',
    lazy: () =>
      import('../pages/CertificationSectionLayout').then((m) => ({
        Component: m.TestSetLayout,
      })),
    children: [
      {
        index: true,
        lazy: () =>
          import('../pages/TestSetPage').then((m) => ({ Component: m.TestSetPage })),
      },
      {
        path: 'runs',
        lazy: () =>
          import('../pages/CertificationRunPage').then((m) => ({
            Component: m.CertificationRunPage,
          })),
      },
      {
        path: 'libros',
        lazy: () =>
          import('../pages/LibrosPage').then((m) => ({ Component: m.LibrosPage })),
      },
      {
        path: 'avance',
        lazy: () =>
          import('../pages/CertificationAvancePage').then((m) => ({
            Component: m.DeclareAvancePage,
          })),
      },
    ],
  },
  {
    path: 'certification/simulation',
    lazy: () =>
      import('../pages/CertificationSectionLayout').then((m) => ({
        Component: m.SimulationLayout,
      })),
    children: [
      {
        index: true,
        lazy: () =>
          import('../pages/SimulationPage').then((m) => ({ Component: m.SimulationPage })),
      },
      {
        // A simulación's runs, under its own step so the sidebar keeps
        // «Simulación» lit and the test set's stepper is not drawn above them.
        // Same screen as `test-set/runs`, filtered to simulaciones; see
        // `run-path.ts`.
        path: 'runs',
        lazy: () =>
          import('../pages/CertificationRunPage').then((m) => ({
            Component: m.CertificationRunPage,
          })),
      },
      {
        // «Declarar avance» for PASO SIMULACION: the test set's declare screen,
        // scoped to the «SET DE SIMULACION» row.
        path: 'avance',
        lazy: () =>
          import('../pages/CertificationAvancePage').then((m) => ({
            Component: m.DeclareSimulationPage,
          })),
      },
    ],
  },
  {
    path: 'certification/intercambio',
    lazy: () =>
      import('../pages/IntercambioPage').then((m) => ({ Component: m.IntercambioPage })),
  },
  {
    path: 'certification/muestras',
    lazy: () => import('../pages/MuestrasPage').then((m) => ({ Component: m.MuestrasPage })),
  },
  {
    path: 'certification/declaracion',
    lazy: () =>
      import('../pages/CertificationSectionLayout').then((m) => ({
        Component: m.DeclarationLayout,
      })),
    children: [
      {
        index: true,
        lazy: () =>
          import('../pages/DeclarationPage').then((m) => ({ Component: m.DeclarationPage })),
      },
      {
        path: 'cuadratura',
        lazy: () =>
          import('../pages/CuadraturaPage').then((m) => ({
            Component: m.CuadraturaPage,
          })),
      },
    ],
  },
];
